import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import httpClient, { extractErrorMessage } from '../Services/httpClient';

const TYPE_LABELS = {
    Purchase: 'Compra de coins',
    ServicePaymentDebit: 'Pagamento de serviço',
    ServicePaymentCredit: 'Recebimento de serviço',
    PlatformFee: 'Taxa da plataforma',
    Refund: 'Estorno',
    WithdrawalRequested: 'Saque solicitado',
    WithdrawalFee: 'Taxa de saque',
    PlatformServiceCharge: 'Cobrança de funcionalidade'
};

const WITHDRAWAL_STATUS_LABELS = {
    Pending: 'Pendente',
    Approved: 'Aprovado',
    Rejected: 'Rejeitado',
    Completed: 'Concluído',
    Failed: 'Falhou'
};

const WITHDRAWAL_STATUS_COLORS = {
    Pending: { backgroundColor: '#fff3e0', color: '#f9a825' },
    Approved: { backgroundColor: '#e3f2fd', color: '#1976d2' },
    Rejected: { backgroundColor: '#fdecea', color: '#c62828' },
    Completed: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
    Failed: { backgroundColor: '#fdecea', color: '#c62828' }
};

const PAGE_SIZE = 20;

const TransactionsPanel = () => {
    const [items, setItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('');
    const [type, setType] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [refundTarget, setRefundTarget] = useState(null);
    const [isRefunding, setIsRefunding] = useState(false);

    const [withdrawalRequestsById, setWithdrawalRequestsById] = useState({});
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [isProcessingWithdrawal, setIsProcessingWithdrawal] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [transactionsResponse, withdrawalRequestsResponse] = await Promise.all([
                httpClient.get('/backoffice/transactions', {
                    params: { page, pageSize: PAGE_SIZE, status: status || undefined, type: type || undefined }
                }),
                httpClient.get('/backoffice/withdrawal-requests')
            ]);
            setItems(transactionsResponse.data.items);
            setTotalCount(transactionsResponse.data.totalCount);
            setWithdrawalRequestsById(
                Object.fromEntries(withdrawalRequestsResponse.data.map((request) => [request.id, request]))
            );
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    }, [page, status, type]);

    useEffect(() => {
        load();
    }, [load]);

    const handleFilterChange = (setter) => (event) => {
        setPage(1);
        setter(event.target.value);
    };

    const handleConfirmRefund = async () => {
        if (!refundTarget) return;
        setIsRefunding(true);
        setError(null);
        setSuccess(null);
        try {
            await httpClient.post(`/backoffice/transactions/${refundTarget.id}/refund`);
            setSuccess(`Transação ${refundTarget.id} estornada com sucesso.`);
            setRefundTarget(null);
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsRefunding(false);
        }
    };

    const handleApproveWithdrawal = async (requestId) => {
        setError(null);
        setSuccess(null);
        try {
            await httpClient.put(`/backoffice/withdrawal-requests/${requestId}/approve`);
            setSuccess('Saque aprovado.');
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    const handleOpenRejectWithdrawal = (requestId) => {
        setRejectTarget(requestId);
        setRejectReason('');
    };

    const handleConfirmRejectWithdrawal = async () => {
        if (!rejectTarget || !rejectReason.trim()) return;
        setIsProcessingWithdrawal(true);
        setError(null);
        setSuccess(null);
        try {
            await httpClient.put(`/backoffice/withdrawal-requests/${rejectTarget}/reject`, rejectReason, {
                headers: { 'Content-Type': 'application/json' }
            });
            setSuccess('Saque rejeitado.');
            setRejectTarget(null);
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsProcessingWithdrawal(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ color: '#f77513', fontWeight: 'bold', mb: 2 }}>
                Transações da plataforma
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField select label="Status" size="small" value={status} onChange={handleFilterChange(setStatus)} sx={{ minWidth: 180 }}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Reversed">Reversed</MenuItem>
                </TextField>
                <TextField select label="Tipo" size="small" value={type} onChange={handleFilterChange(setType)} sx={{ minWidth: 220 }}>
                    <MenuItem value="">Todos</MenuItem>
                    {Object.entries(TYPE_LABELS).map(([key, label]) => (
                        <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))}
                </TextField>
            </Box>

            {isLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={28} style={{ color: '#f77513' }} />
                </Box>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '14px' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow style={{ backgroundColor: '#fff4ec' }}>
                                <TableCell>Data</TableCell>
                                <TableCell>Owner</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell align="right">Valor</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" style={{ color: '#aaa', padding: '24px' }}>
                                        Nenhuma transação encontrada
                                    </TableCell>
                                </TableRow>
                            )}
                            {items.map((item) => {
                                const isWithdrawalRequest = item.type === 'WithdrawalRequested';
                                const withdrawalRequest = isWithdrawalRequest
                                    ? withdrawalRequestsById[item.relatedTransactionId]
                                    : null;

                                return (
                                    <TableRow key={item.id}>
                                        <TableCell>{new Date(item.createdAt).toLocaleString('pt-BR')}</TableCell>
                                        <TableCell style={{ fontFamily: 'monospace', fontSize: '12px' }}>{item.ownerId}</TableCell>
                                        <TableCell>{TYPE_LABELS[item.type] || item.type}</TableCell>
                                        <TableCell>{item.description}</TableCell>
                                        <TableCell align="right">{item.amount} 🍋</TableCell>
                                        <TableCell>
                                            {isWithdrawalRequest && withdrawalRequest ? (
                                                <Chip
                                                    label={WITHDRAWAL_STATUS_LABELS[withdrawalRequest.status] || withdrawalRequest.status}
                                                    size="small"
                                                    style={WITHDRAWAL_STATUS_COLORS[withdrawalRequest.status]}
                                                />
                                            ) : (
                                                <Chip
                                                    label={item.status}
                                                    size="small"
                                                    style={item.status === 'Reversed'
                                                        ? { backgroundColor: '#fdecea', color: '#c62828' }
                                                        : { backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isWithdrawalRequest ? (
                                                withdrawalRequest?.status === 'Pending' && (
                                                    <>
                                                        <Button size="small" style={{ color: '#2e7d32' }} onClick={() => handleApproveWithdrawal(withdrawalRequest.id)}>
                                                            Aprovar
                                                        </Button>
                                                        <Button size="small" style={{ color: '#c62828' }} onClick={() => handleOpenRejectWithdrawal(withdrawalRequest.id)}>
                                                            Rejeitar
                                                        </Button>
                                                    </>
                                                )
                                            ) : (
                                                <Button
                                                    size="small"
                                                    disabled={item.status === 'Reversed'}
                                                    style={{ color: item.status === 'Reversed' ? undefined : '#f77513' }}
                                                    onClick={() => setRefundTarget(item)}
                                                >
                                                    Estornar
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Página {page} de {totalPages} ({totalCount} transações)
                </Typography>
                <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    <ChevronLeftIcon />
                </IconButton>
                <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    <ChevronRightIcon />
                </IconButton>
            </Box>

            <Dialog open={!!refundTarget} onClose={isRefunding ? undefined : () => setRefundTarget(null)}>
                <DialogTitle>Confirmar estorno</DialogTitle>
                <DialogContent>
                    {refundTarget && (
                        <Typography variant="body2">
                            Estornar a transação <strong>{TYPE_LABELS[refundTarget.type] || refundTarget.type}</strong> de{' '}
                            <strong>{refundTarget.amount} 🍋</strong> ({refundTarget.description})?
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRefundTarget(null)} disabled={isRefunding} style={{ color: '#666' }}>Cancelar</Button>
                    <Button
                        onClick={handleConfirmRefund}
                        disabled={isRefunding}
                        variant="contained"
                        color="error"
                        startIcon={isRefunding ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        Confirmar estorno
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!rejectTarget} onClose={isProcessingWithdrawal ? undefined : () => setRejectTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle>Rejeitar solicitação de saque</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        O saldo será devolvido ao consultor. Informe o motivo da rejeição:
                    </Typography>
                    <TextField
                        label="Motivo"
                        fullWidth
                        multiline
                        minRows={2}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectTarget(null)} disabled={isProcessingWithdrawal} style={{ color: '#666' }}>Cancelar</Button>
                    <Button
                        onClick={handleConfirmRejectWithdrawal}
                        disabled={isProcessingWithdrawal || !rejectReason.trim()}
                        variant="contained"
                        color="error"
                        startIcon={isProcessingWithdrawal ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        Confirmar rejeição
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TransactionsPanel;
